<template>
    <nav class="subnav"
         role="navigation"
         aria-label="Menu"
         @keyup.esc="onHamburgerEscape">
        <hh-btn :class="`subnav__hamburger --${showSubMenu ? 'shown' : 'hidden'}`"
                tabindex="0"
                text="Menu"
                icon-class="fas fa-bars"
                type="button"
                aria-label="Menu"
                @click="onHamburgerClickOrEnter"
                @keypress.enter.prevent="onHamburgerClickOrEnter" />
        <ul ref="subnavLinkWrapper"
            :class="`subnav__nav --${showSubMenu ? 'shown' : 'hidden'}`">
            <subnav-destination v-if="showLoginLink"
                                :link="logInLink"
                                class="subnav__nav__link --log-in --mobile-only"
                                @click="onSubMenuItemClick" />
            <subnav-destination v-if="showRegisterLink"
                                :link="registerLink"
                                class="subnav__nav__link --register --mobile-only"
                                @click="onSubMenuItemClick" />
            <subnav-destination v-if="userSignedIn"
                                :link="dashboardLink"
                                class="subnav__nav__link --dashboard"
                                @click="onSubMenuItemClick" />
            <subnav-destination v-if="showLogOutLink"
                                :link="logOutLink"
                                class="subnav__nav__link --log-out"
                                @click="onSubMenuItemClick" />
            <li class="subnav__nav__separator" />
            <subnav-destination v-for="navItem in items"
                                :link="navItem"
                                :key="navItem.key"
                                class="subnav__nav__link --mobile-only"
                                @click="onSubMenuItemClick" />
            <li class="subnav__nav__separator --mobile-only" />
            <li class="subnav__policies-item">
                <app-policies ref="subnavPolicies"
                              class="subnav__policies"
                              :handle-events-externally="true"
                              @click="onSubMenuItemClick" />
            </li>
        </ul>
    </nav>
</template>

<script>

import PathManager from 'uri-path-manager'
import Vuex from 'vuex'
import Window from 'window-wrapper'

import NavigationLink from '../../models/navigation-link'

import LoopedTabbingControl from '../looped-tabbing-control'
import AppPolicies from '../../components/app-core/app-policies'
import SubnavDestination from './subnav-destination'
import LogOutConfirmation from '../../components/auth/modals/log-out-confirmation'
import ResourcesHelper from '@/components/resources-helper'

export default {

    mixins: [
        LoopedTabbingControl,
        ResourcesHelper,
    ],

    props: {
        items: {
            type: Array,
            required: true,
        },
    },

    computed: {

        ...Vuex.mapGetters([
            'userSignedIn',
        ]),

        ...Vuex.mapState([
            'property',
        ]),

        logInLink() {
            return new NavigationLink(this.commonResources.login, 'LogInFromSubnav', { appLink: true })
        },

        logOutLink() {
            return new NavigationLink(this.commonResources.logout, LogOutConfirmation, { modalLink: true })
        },

        dashboardLink() {
            return new NavigationLink(this.allResources.views.dashboard.title, 'Dashboard', { appLink: true })
        },

        registerLink() {
            return new NavigationLink(this.commonResources.register, 'Register', { appLink: true })
        },

        showLoginLink() {
            return !this.showLogOutLink && ![ 'LogIn', 'LogInWithContext', 'LogInFromSubnav' ].includes(this.$route.name)
        },

        showLogOutLink() {
            return this.userSignedIn
        },

        showRegisterLink() {
            return !this.showLogOutLink && this.property != null && this.$route.name !== 'Register'
        },
    },

    data() {
        return {
            pathManager: null,
            showSubMenu: false,
        }
    },

    watch: {

        showSubMenu() {
            this.tabbableRootNodeRef = this.$refs.subnavLinkWrapper
            this.bindTabbingEvents()
        },
    },

    beforeDestroy() {
        // noinspection JSCheckFunctionSignatures
        Window.removeEventListener('popstate', this.onBrowserBack)
    },

    beforeMount() {
        // noinspection JSCheckFunctionSignatures
        Window.addEventListener('popstate', this.onBrowserBack)
    },

    mounted() {
        this.pathManager = new PathManager()
        this.$eventbus.$on(this.$appevents.hideSubMenu, () => {
            if (this.showSubMenu) this._toggleSubMenu()
        })
        this.$eventbus.$on(this.$appevents._toggleSubMenu, this._toggleSubMenu)
        this.tabbableRootNodeRef = this.$refs.subnavLinkWrapper
        this.bindTabbingEvents()
    },

    unmounted() {
        this.$eventbus.$off(this.$appevents.hideSubMenu)
        this.$eventbus.$off(this.$appevents._toggleSubMenu)
    },

    methods: {

        _hideSubMenu(suppressBack = false) {
            this.showSubMenu = false
            if (!suppressBack) history.back()
            this.$eventbus.$emit(this.$appevents.hideMask)
        },

        _showSubMenu() {
            this.$eventbus.$emit(this.$appevents.hideModal, () => {
                this.showSubMenu = true
                history.pushState(null, 'Subnav', '#nav')
                this.$eventbus.$emit(this.$appevents.showMask, 'standard-mask', {
                    onMaskClick: () => this._toggleSubMenu(),
                    cssModifier: 'nav',
                })
            })
        },

        _toggleSubMenu(suppressBack = false) {
            const showSubMenu = !this.showSubMenu

            if (showSubMenu)
                this._showSubMenu()
            else
                this._hideSubMenu(suppressBack)
        },

        onBrowserBack() {
            if (this.showSubMenu) {
                this.showSubMenu = false
                this.$eventbus.$emit(this.$appevents.hideMask)
            }

            if (this.pathManager.href().includes('#nav'))
                history.back()
        },

        onHamburgerClickOrEnter() {
            this._toggleSubMenu()
        },

        onHamburgerEscape() {
            this._hideSubMenu()
        },

        onSubMenuItemClick($event) {
            if ($event.linkTarget === 'app')
                this._toggleSubMenu(true)
            else
                this._toggleSubMenu()

            if ($event.action) $event.action()
        },
    },

    components: {
        AppPolicies,
        SubnavDestination,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\header\_main-nav.scss

.subnav {

    @include element('hamburger') {
        display: inline-block;
        padding: 0.2rem 0.6rem !important;
        margin-top: 0 !important;

        &.--shown > .fa-bars {
            transform: rotate(90deg);
            transition: 300ms ease all;
        }

        &.--hidden > .fa-bars {
            transform: rotate(0);
            transition: 300ms ease all;
        }

        .button__icon--pre {
            padding-right: 0;
        }

        .button__text {
            display: none;

            @include bp-min-small {
                display: inline;
                padding-left: 0.4em;
            }
        }
    }

    @include element('nav') {
        list-style: none;
        margin: 0;
        cursor: default;
        position: absolute;
        z-index: z-index('nav-mask');
        top: header-height('base');
        left: 0;
        height: calc(100vh - header-height('small'));
        overflow-y: auto;
        width: 100%;
        padding: $base-gutter;


        @include bp-min-small {
            left: auto;
            text-align: right;
            width: auto;
            min-width: 180px;
            height: auto;
        }

        &.--shown {
            display: block;
        }

        &.--hidden {
            display: none;
        }

        @include element('separator') {
            margin: 3px 0;
            height: 2px;

            &.--mobile-only {

                @include bp-min-small {
                    display: none;
                }
            }
        }

        @include element('link') {

            &.--mobile-only {

                @include bp-min-small {
                    display: none;
                }
            }
        }
    }

    @include element('policies-item') {

        @include bp-min-small {
            display: none;
        }
    }

    @include element('policies') {
        margin-left: rem(-12.5px);
        flex-direction: column;

        li {
            position: relative;
            padding: 0 0 0 0.8rem;

            a {
                display: inline-block;
                width: 100%;
                padding: 1rem 0 0 0;
                position: relative;

                &:hover, &:active {
                    text-decoration: underline;
                }
            }
        }
    }
}

.touch {

    .subnav {
        @include element('policies') {

            li {
                a {
                    text-decoration: underline;
                }
            }
        }
    }
}

</style>
